import { Injectable } from '@angular/core';
import { UploadService } from './upload.service';
import { AgeasService } from './ageas.service';
import { TranquilidadeService } from './tranquilidade.service';
import { ImageService } from './image.service';
import { CustomerService } from './customer.service';
import { CommonService } from './common.service';
import { MessagecenterService } from './messagecenter.service';
import { AlertMessageService } from './alert-message.service';
import { FilesClass, DownloadedFile } from '../models/uploads-class';
import { EmailClass, AttachmentsClass } from '../models/common-class';
import { HTML_PARTILHA_DOCUMENTO } from '../components/email-marketing/email-templates-manager/predefined-templates';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import Swal from './swal-compat';

/**
 * FileHandlingService
 *
 * Service focado em operações com ficheiros:
 * - Download de ficheiros (normais, AGEAS, Tranquilidade)
 * - Partilha de ficheiros por email
 * - Upload e gestão de documentos
 */
@Injectable({
  providedIn: 'root',
})
export class FileHandlingService {
  constructor(
    private uploadService: UploadService,
    private ageasService: AgeasService,
    private tranquilidadeService: TranquilidadeService,
    private imageService: ImageService,
    private customerService: CustomerService,
    private commonService: CommonService,
    private messagecenterService: MessagecenterService,
    private alerts: AlertMessageService,
  ) {}

  /**
   * Obtém o Blob e o nome de ficheiro para qualquer documento suportado
   */
  async getFileBlob(row: FilesClass | any): Promise<{ blob: Blob; filename: string }> {
    if (!row.VirtualDoc && row.FilePath && row.FilePath !== 'Recibo' && row.FilePath !== 'CartaVerde') {
      const file: any = await firstValueFrom(this.uploadService.download(row.FilePath));
      const baseName = (row.DocumentName || row.Description || 'documento').trim();
      const filename = baseName.toLowerCase().endsWith('.pdf') ? baseName : `${baseName}.pdf`;
      const blob = file instanceof Blob ? file : new Blob([file], { type: file.type || 'application/pdf' });
      return { blob, filename };
    }

    // Documentos AGEAS
    const docId =
      row.documentId ||
      (row.FilePath && row.FilePath !== 'Recibo' && row.FilePath !== 'CartaVerde' && !row.FilePath.includes('/')
        ? row.FilePath
        : null);
    const isAgeas = Number(row.idCompanie) === 2 || (docId && row.PolicyNumber && !row.idCompanie);
    if (isAgeas && docId && row.PolicyNumber) {
      const res: any = await firstValueFrom(this.ageasService.AGEASdownload(row.PolicyNumber, docId));
      const downloadedFile = new DownloadedFile(res);
      if (!downloadedFile.document) {
        throw new Error('O servidor não devolveu o conteúdo do documento AGEAS.');
      }
      const fileBlob = this.imageService.base64ToBlob(downloadedFile.document, downloadedFile.contentType);
      const baseName = (downloadedFile.name || row.Description || row.DocumentName || 'documento').trim();
      const filename = baseName.toLowerCase().endsWith('.pdf') ? baseName : `${baseName}.pdf`;
      return { blob: fileBlob, filename };
    }

    // Documentos Tranquilidade
    if (Number(row.idCompanie) === 3 || row.FilePath === 'CartaVerde' || row.FilePath === 'Recibo') {
      let blob: Blob;
      if (row.FilePath === 'Recibo') {
        blob = await firstValueFrom(
          this.tranquilidadeService.TranquilidadedownloadObterDocumentoRecibo(
            row.NrRecibo,
            row.ValorRecibo,
            row.PolicyNumber,
            row.Tipo,
          ),
        );
      } else if (row.FilePath === 'CartaVerde') {
        blob = await firstValueFrom(
          this.tranquilidadeService.TranquilidadedownloadObterDocumentoCartaVerde(row.NrRecibo, row.PolicyNumber, ''),
        );
      } else {
        throw new Error('Tipo de documento desconhecido para Tranquilidade');
      }
      const baseName = (row.DocumentName || row.Description || 'documento').trim();
      const filename = baseName.toLowerCase().endsWith('.pdf') ? baseName : `${baseName}.pdf`;
      return { blob, filename };
    }

    throw new Error('Tipo de ficheiro não suportado');
  }

  /**
   * Download de ficheiro (suporta: normal, AGEAS, Tranquilidade)
   */
  async download(row: FilesClass | any): Promise<void> {
    row.isWaitFile = true;

    try {
      if (!row.VirtualDoc) {
        if (!row.FilePath) throw new Error('Caminho do arquivo não fornecido');

        const file = await firstValueFrom(this.uploadService.download(row.FilePath));
        let downloadName = row.Description || row.DocumentName || 'documento';
        const extMatch = (row.FilePath || row.DocumentName || '').match(/\.([a-zA-Z0-9]+)(?:[?#]|$)/);
        const ext = extMatch ? `.${extMatch[1]}` : '';
        if (ext && !downloadName.toLowerCase().endsWith(ext.toLowerCase())) {
          downloadName = `${downloadName}${ext}`;
        }
        await this.uploadService.handleFile(file, downloadName);
        return;
      }

      // Documentos Virtuais (AGEAS / Tranquilidade)
      const { blob } = await this.getFileBlob(row);
      const fileURL = URL.createObjectURL(blob);
      const newWindow = window.open(fileURL, '_blank');

      if (!newWindow) {
        console.warn('⚠️ [FileHandling] Pop-up bloqueado pelo browser.');
        Swal.fire(
          'Atenção',
          'O bloqueador de pop-ups está ativado. Por favor, permita pop-ups para este site.',
          'warning',
        );
      }
    } catch (error: any) {
      console.error(error);

      // Tratamento específico para erro 404 (documento não disponível)
      if (error?.status === 404 || error?.error?.includes?.('404') || error?.message?.includes?.('404')) {
        Swal.fire(
          'Documento Não Disponível',
          'Este documento não está disponível na seguradora. Por favor, contacte-nos se necessitar deste documento.',
          'info',
        );
      } else {
        Swal.fire('Atenção', 'Ficheiro desconhecido ou danificado, por favor, tente novamente.', 'warning');
      }
    } finally {
      row.isWaitFile = false;
    }
  }

  /**
   * Descarrega múltiplos ficheiros empacotados num arquivo ZIP em memória
   */
  async downloadAsZip(files: (FilesClass | any)[], zipName = 'Documentos.zip'): Promise<void> {
    if (!files || files.length === 0) {
      this.alerts.warning('Não existem documentos para descarregar.', 'Atenção');
      return;
    }

    const validFiles = files.filter(f => !f.DateEnd && (f.FilePath || f.VirtualDoc));
    if (validFiles.length === 0) {
      this.alerts.warning('Não existem documentos válidos para descarregar.', 'Atenção');
      return;
    }

    console.log(`📦 [FileHandling] A preparar download ZIP de ${validFiles.length} documento(s)...`);

    try {
      const JSZipModule = await import('jszip');
      const JSZip = (JSZipModule as any).default || JSZipModule;
      const zip = new JSZip();

      const usedNames = new Set<string>();
      let addedCount = 0;

      for (const row of validFiles) {
        try {
          const { blob, filename } = await this.getFileBlob(row);
          let safeName = filename.replace(/[\\/:*?"<>|]/g, '_');
          let finalName = safeName;
          let counter = 1;
          const dotIdx = safeName.lastIndexOf('.');
          const base = dotIdx !== -1 ? safeName.substring(0, dotIdx) : safeName;
          const ext = dotIdx !== -1 ? safeName.substring(dotIdx) : '';

          while (usedNames.has(finalName.toLowerCase())) {
            finalName = `${base}_${counter}${ext}`;
            counter++;
          }
          usedNames.add(finalName.toLowerCase());

          zip.file(finalName, blob);
          addedCount++;
        } catch (fileErr) {
          console.warn(
            `⚠️ [FileHandling] Não foi possível incluir o ficheiro ${row.DocumentName || row.Description} no ZIP:`,
            fileErr,
          );
        }
      }

      if (addedCount === 0) {
        this.alerts.error('Não foi possível obter nenhum dos documentos para criar o arquivo ZIP.', 'Atenção');
        return;
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const finalZipName = zipName.endsWith('.zip') ? zipName : `${zipName}.zip`;
      await this.uploadService.handleFile(zipBlob, finalZipName);
      console.log(`✅ [FileHandling] Arquivo ZIP "${finalZipName}" gerado com ${addedCount} documento(s).`);
    } catch (err) {
      console.error('❌ [FileHandling] Erro ao gerar ficheiro ZIP:', err);
      this.alerts.error('Ocorreu um erro ao gerar o arquivo ZIP dos documentos.', 'Atenção');
    }
  }

  /**
   * Download de ficheiro via path
   */
  downloadFilePath(path: string, Filename: string) {
    this.uploadService.download(path + Filename).subscribe({
      next: (res: any) => {
        this.uploadService.handleFile(res, Filename);
      },
      error: error => {
        console.log(error);
        Swal.fire('Atenção', 'Ficheiro desconhecido ou danificado, por favor, tente novamente.', 'warning');
      },
    });
  }

  /**
   * Partilhar ficheiro por email com dialog
   */
  async fileshare(row: FilesClass) {
    try {
      let Nome = '';
      let Email = '';

      const dadosCliente = await firstValueFrom(this.customerService.ClientAreaGetCustomer(row.idCustomer));
      if (dadosCliente && dadosCliente[0]) {
        Nome = dadosCliente[0].Name || '';
        Email = dadosCliente[0].Email || '';
      }

      // Escapar aspas para uso seguro em atributos HTML
      const safeNome = Nome.replace(/"/g, '&quot;');
      const safeEmail = Email.replace(/"/g, '&quot;');

      const result = await (Swal.fire as any)({
        title: 'Partilhar Documento',
        html: `
        <div style="font-size: 13px; flex-direction: column; width: 100%;">
            <div style="width: 100%; text-align: left;">
                <span style="font-weight: bold;">Nome:</span><br>
                <input id="swal-input1" value="${safeNome}"
                    style="width: 100%; height: 36px; font-size: 14px; border: 1px solid #c4c4c4; border-radius: 4px; padding: 0 8px; box-sizing: border-box;">
            </div>
            <div style="width: 100%; text-align: left; margin-top: 10px;">
                <span style="font-weight: bold;">Email:</span><br>
                <input id="swal-input2" type="email" value="${safeEmail}"
                    style="width: 100%; height: 36px; font-size: 14px; border: 1px solid #c4c4c4; border-radius: 4px; padding: 0 8px; box-sizing: border-box;">
            </div>
            <div id="swal-validation-msg" style="color: #dc3545; font-size: 12px; margin-top: 6px; display: none;"></div>
        </div>
    `,
        focusConfirm: false,
        showCancelButton: true,
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Enviar',
        width: '350px',
      });

      if (result && result.isConfirmed) {
        // Ler valores capturados pelo sv-message antes de fechar o modal
        const htmlValues = result.htmlInputValues || {};
        const nome = htmlValues['swal-input1'] || Nome;
        const email = htmlValues['swal-input2'] || Email;

        // Validar email
        if (!email || !/\S+@\S+\.\S+/.test(email)) {
          Swal.fire('Atenção', 'Email inválido!', 'warning');
          return;
        }

        // Feedback visual imediato durante o processamento do documento e envio do email
        Swal.fire({
          title: 'A enviar documento...',
          html: 'Por favor, aguarde enquanto o documento é processado e enviado.',
          showConfirmButton: false,
          showCancelButton: false,
        });

        let documento = row.DocumentName || row.Description;
        const nomeParts = (nome || '').trim().split(/\s+/).filter(Boolean);
        const primeiroNome = nomeParts[0] || nome || '';
        const nomeCliente = nomeParts.length > 1 ? `${nomeParts[0]} ${nomeParts[nomeParts.length - 1]}` : primeiroNome;
        const descricaoDoc = row.Description || row.DocumentName || 'Documento';

        const { subject, html } = await this.resolveDocumentShareEmailContent({
          nomeCliente,
          primeiroNome,
          nomeCompleto: (nome || '').trim() || nomeCliente,
          emailCliente: email,
          descricaoDocumento: descricaoDoc,
          apoliceNumero: row.PolicyNumber || '',
          linkAreaCliente: 'https://segurosvirtual.pt/login',
        });

        const emailObj = new EmailClass();
        emailObj.to = email;
        emailObj.subject = subject;
        emailObj.bcc = '';
        emailObj.NIF = '';
        emailObj.html = html;
        emailObj.text = html;

        const attachment = new AttachmentsClass();
        attachment.filename = documento;
        attachment.pathexternal = false;

        const isVirtual =
          row.VirtualDoc ||
          row.FilePath === 'Recibo' ||
          row.FilePath === 'CartaVerde' ||
          Number(row.idCompanie) === 2 ||
          Number(row.idCompanie) === 3;

        if (isVirtual) {
          try {
            const fileBlobResult = await this.getFileBlob(row);
            const blob = fileBlobResult?.blob;
            if (!blob) {
              throw new Error('Não foi possível obter o ficheiro da seguradora.');
            }
            const buffer = await blob.arrayBuffer();
            attachment.filename = fileBlobResult.filename || documento || 'Documento.pdf';
            attachment.buffer = Buffer.from(buffer);
            attachment.contentType = blob.type || 'application/pdf';
            attachment.pathexternal = false;
          } catch (fileErr: any) {
            console.error('❌ [FileHandling] Erro ao descarregar documento virtual para partilha:', fileErr);
            Swal.fire(
              'Atenção',
              'Não foi possível obter o documento da seguradora para envio. Por favor, tente novamente.',
              'warning',
            );
            return;
          }
        } else {
          attachment.filename = row.DocumentName || row.Description;
          attachment.path = row.FilePath;
          attachment.pathexternal = false;
        }

        emailObj.attachments = [attachment];

        this.commonService.SendEmail(emailObj).subscribe({
          next: dados => {
            console.log('[FileHandling] Email enviado:', dados);
            Swal.fire('Documento Enviado!', 'O documento foi enviado com sucesso.', 'success');
          },
          error: error => {
            console.error('[FileHandling] Erro ao enviar email:', error);
            this.alerts.error('ERRORS.REQUEST_FAILED', 'COMMON.ALERTS.ATTENTION');
          },
        });
      }
    } catch (error) {
      this.alerts.error('ERRORS.REQUEST_FAILED', 'COMMON.ALERTS.ATTENTION');
      console.log(error);
    }
  }

  /**
   * Obtém o template "Partilha de Documento" da lista central de Templates de Email (/crm/email-templates)
   * e substitui as variáveis dinâmicas, usando o design oficial como fallback.
   */
  private async resolveDocumentShareEmailContent(vars: {
    nomeCliente: string;
    primeiroNome: string;
    nomeCompleto: string;
    emailCliente: string;
    descricaoDocumento: string;
    apoliceNumero: string;
    linkAreaCliente: string;
  }): Promise<{ subject: string; html: string }> {
    const defaultSubject = `Documento: ${vars.descricaoDocumento}`;
    let rawSubject = defaultSubject;
    let rawHtml = HTML_PARTILHA_DOCUMENTO.trim();

    try {
      const templates = await firstValueFrom(
        this.messagecenterService.GetEmailTemplates().pipe(catchError(() => of([]))),
      );
      if (Array.isArray(templates) && templates.length > 0) {
        const normalizeStr = (s: string) =>
          (s || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim();

        const isDocShareTemplate = (t: any) => {
          const name = normalizeStr(t?.Name);
          return (
            name === 'partilha de documento' ||
            name === 'partilhar documento' ||
            name.includes('partilha de documento') ||
            name.includes('partilhar documento') ||
            t?.TemplateKey === 'document_share' ||
            Number(t?.ID || t?.idEmailTemplate) === 990003
          );
        };

        // Priorizar template próprio caso exista, senão o template oficial da lista
        const matched =
          templates.find(t => isDocShareTemplate(t) && t?.IsOwnTemplate) || templates.find(t => isDocShareTemplate(t));

        if (matched) {
          if (matched.Subject && String(matched.Subject).trim()) {
            rawSubject = String(matched.Subject).trim();
          }
          if (matched.Body && typeof matched.Body === 'string') {
            const cleanBody = matched.Body.replace(/<!--SV_TEMPLATE_META:[\s\S]*?-->/g, '').trim();
            if (cleanBody.startsWith('{')) {
              try {
                const parsed = JSON.parse(cleanBody);
                if (parsed && typeof parsed.html === 'string' && parsed.html.trim()) {
                  rawHtml = parsed.html.trim();
                }
              } catch (_e) {}
            } else if (cleanBody) {
              rawHtml = cleanBody;
            }
          }
        }
      }
    } catch (_err) {
      // Fallback silencioso para HTML_PARTILHA_DOCUMENTO
    }

    const replaceVars = (input: string): string => {
      if (!input) return '';
      return input
        .replace(/\{\{\s*NomeCliente\s*\}\}/gi, vars.nomeCliente)
        .replace(/\{\{\s*TomadorNome\s*\}\}/gi, vars.nomeCliente)
        .replace(/\{\{\s*nome_cliente\s*\}\}/gi, vars.nomeCliente)
        .replace(/\{\{\s*Nome\s*\}\}/gi, vars.nomeCliente)
        .replace(/\[\[\s*FirstLastName\s*\]\]/gi, vars.nomeCliente)
        .replace(/\{\{\s*PrimeiroNome\s*\}\}/gi, vars.primeiroNome)
        .replace(/\[\[\s*FirstName\s*\]\]/gi, vars.primeiroNome)
        .replace(/\{\{\s*NomeCompleto\s*\}\}/gi, vars.nomeCompleto)
        .replace(/\[\[\s*Name\s*\]\]/gi, vars.nomeCompleto)
        .replace(/\{\{\s*EmailCliente\s*\}\}/gi, vars.emailCliente)
        .replace(/\{\{\s*email\s*\}\}/gi, vars.emailCliente)
        .replace(/\[\[\s*Email\s*\]\]/gi, vars.emailCliente)
        .replace(/\{\{\s*DescricaoDocumento\s*\}\}/gi, vars.descricaoDocumento)
        .replace(/\{\{\s*DocumentoNome\s*\}\}/gi, vars.descricaoDocumento)
        .replace(/\{\{\s*Documento\s*\}\}/gi, vars.descricaoDocumento)
        .replace(/\{\{\s*ApoliceNumero\s*\}\}/gi, vars.apoliceNumero)
        .replace(/\{\{\s*NumeroApolice\s*\}\}/gi, vars.apoliceNumero)
        .replace(/\[\[\s*PolicyNumber\s*\]\]/gi, vars.apoliceNumero)
        .replace(/\{\{\s*LinkAreaCliente\s*\}\}/gi, vars.linkAreaCliente)
        .replace(/\[\[\s*CustomerAreaLink\s*\]\]/gi, vars.linkAreaCliente)
        .replace(/\[\[\s*Link\s*\]\]/gi, vars.linkAreaCliente);
    };

    return {
      subject: replaceVars(rawSubject) || defaultSubject,
      html: replaceVars(rawHtml),
    };
  }

  /**
   * Remove extensão de um filename
   */
  retiraextensao(_type: string, filename): string {
    if (typeof filename !== 'string') return;
    var frags = filename.split('.');
    return frags.splice(0, frags.length - 1).join('.');
  }
}
